import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import SharedFooter from "@/components/SharedFooter";
import { EditableText, EditableSection, useAdmin } from "@/components/AdminContentEditor";
import {
  ArrowRight,
  Heart,
  Leaf,
  Droplets,
  Sun,
  Moon,
  Wind,
  Flame,
  Music,
  Sparkles,
  Users,
  Star,
  Clock,
  MessageCircle,
  MapPin,
  Calendar,
  Compass,
  Mountain,
  Waves,
  TreePine
} from "lucide-react";

const WellnessSalesPage = () => {
  const [showEarlyAccessModal, setShowEarlyAccessModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("community");
  const [hoveredPreference, setHoveredPreference] = useState(null);
  const { editMode } = useAdmin();

  useEffect(() => {
    document.title = "phygital - For Wellness Communities";
  }, []);

  // Earth tone colors
  const earthTones = {
    sand: "#E8DCC4",
    sage: "#9CAF88",
    terracotta: "#C4977E",
    clay: "#B5A18E",
    moss: "#6B7B5F",
    cream: "#FAF7F2",
    stone: "#8B8178",
    warmBrown: "#8B7355"
  };

  // Wellness categories - Community first, then excursions
  const wellnessCategories = {
    community: {
      name: "Wellness Community",
      icon: Users,
      description: "Build & nurture your wellness tribe",
      preferences: [
        { icon: Heart, label: "Wellness Focus", options: ["Mind", "Body", "Spirit", "All three"] },
        { icon: Sun, label: "Practice Level", options: ["Curious beginner", "Developing", "Established", "Guiding others"] },
        { icon: Users, label: "Connection Style", options: ["Intimate circles", "Group gatherings", "1-on-1 mentorship", "Silent community"] },
        { icon: Moon, label: "Availability", options: ["Mornings", "Evenings", "Weekends", "Flexible"] },
        { icon: Leaf, label: "Interests", options: ["Meditation", "Movement", "Breathwork", "Sound healing", "Nature immersion"] }
      ]
    },
    retreats: {
      name: "Wellness Retreats",
      icon: Mountain,
      description: "Transformative group experiences",
      preferences: [
        { icon: MapPin, label: "Setting", options: ["Mountains", "Ocean", "Desert", "Forest", "Urban sanctuary"] },
        { icon: Calendar, label: "Duration", options: ["Day retreat", "Weekend", "Week-long", "Extended stay"] },
        { icon: Flame, label: "Intensity", options: ["Gentle restoration", "Balanced", "Immersive", "Transformational"] },
        { icon: Users, label: "Group Size", options: ["Intimate (5-10)", "Small (10-20)", "Medium (20-40)", "Large gathering"] },
        { icon: Leaf, label: "Focus", options: ["Detox & cleanse", "Spiritual growth", "Physical renewal", "Creative expression"] }
      ]
    },
    excursions: {
      name: "Wellness Excursions",
      icon: Compass,
      description: "Curated day experiences & adventures",
      preferences: [
        { icon: Sun, label: "Activity Level", options: ["Restful", "Light movement", "Moderate", "Active adventure"] },
        { icon: Waves, label: "Element", options: ["Water-based", "Earth-grounded", "Air & heights", "Fire & heat"] },
        { icon: Clock, label: "Time of Day", options: ["Sunrise", "Daytime", "Sunset", "Stargazing"] },
        { icon: Users, label: "Experience Type", options: ["Solo journey", "Partner", "Small group", "Community outing"] },
        { icon: TreePine, label: "Environment", options: ["Wilderness", "Coastal", "Garden/farm", "Sacred sites"] }
      ]
    },
    circles: {
      name: "Healing Circles",
      icon: Heart,
      description: "Sacred space for collective healing",
      preferences: [
        { icon: Moon, label: "Modality", options: ["Sharing circle", "Sound bath", "Breathwork", "Movement meditation"] },
        { icon: Wind, label: "Energy", options: ["Grounding", "Releasing", "Expanding", "Integrating"] },
        { icon: Users, label: "Facilitation", options: ["Guided", "Co-created", "Peer-led", "Silent witness"] },
        { icon: Flame, label: "Intention", options: ["Stress release", "Grief processing", "Joy cultivation", "Life transitions"] },
        { icon: Leaf, label: "Frequency", options: ["Weekly practice", "New/full moon", "Seasonal", "As needed"] }
      ]
    }
  };

  const currentCategory = wellnessCategories[selectedCategory];

  // Demo community member card
  const demoCommunityMember = {
    name: "Jordan",
    avatar: "J",
    memberSince: "6 months",
    preferences: {
      focus: "Mind & Spirit",
      level: "Developing practice",
      style: "Intimate circles",
      interests: ["Meditation", "Breathwork", "Nature immersion"],
      notes: "Healing from burnout. Prefers morning sessions. Sensitive to strong scents."
    },
    tags: ["Inner Circle", "Retreat alumni", "Monthly member"]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: earthTones.cream }}>
      {/* Header */}
      <header className="border-b border-stone-200 bg-white/60 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" variant="light" />
            <span className="font-semibold text-xl" style={{ color: earthTones.warmBrown }}>phygital</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge 
              variant="outline" 
              className="border-stone-300 bg-stone-50"
              style={{ color: earthTones.moss }}
            >
              <Users className="w-3 h-3 mr-1" />
              Wellness
            </Badge>
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              className="text-white rounded-full px-6"
              style={{ backgroundColor: earthTones.moss }}
              data-testid="join-pilot-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Community Focused */}
      <EditableSection sectionId="wellness-hero" label="Wellness Hero" className="relative overflow-hidden py-24 px-4">
        {/* Organic shapes background */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-10 left-10 w-96 h-96 rounded-full blur-3xl opacity-40"
            style={{ backgroundColor: earthTones.sage }}
          />
          <div 
            className="absolute bottom-10 right-10 w-80 h-80 rounded-full blur-3xl opacity-30"
            style={{ backgroundColor: earthTones.terracotta }}
          />
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: earthTones.sand }}
          />
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge 
            className="mb-6 border"
            style={{ 
              backgroundColor: 'white', 
              color: earthTones.moss,
              borderColor: earthTones.sage 
            }}
          >
            <Users className="w-3 h-3 mr-1" />
            <EditableText sectionId="wellness-hero" field="badge" defaultValue="For Wellness Communities & Excursions" className="inline" />
          </Badge>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-6 tracking-tight" style={{ color: earthTones.warmBrown }}>
            <EditableText sectionId="wellness-hero" field="title_line1" defaultValue="Know your community" className="block" />
            <span className="font-medium" style={{ color: earthTones.moss }}>
              <EditableText sectionId="wellness-hero" field="title_highlight" defaultValue="before they gather." className="inline" />
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: earthTones.stone }}>
            <EditableText 
              sectionId="wellness-hero" 
              field="subtitle" 
              defaultValue="Understand each member's wellness journey, comfort levels, and intentions. Create deeply personalized community experiences and transformative excursions."
              multiline
            />
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              size="lg"
              className="text-white h-14 px-10 rounded-full text-base font-medium shadow-lg"
              style={{ backgroundColor: earthTones.moss }}
              data-testid="hero-join-btn"
            >
              Build Your Community
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-14 px-10 rounded-full text-base"
              style={{ 
                borderColor: earthTones.clay,
                color: earthTones.warmBrown
              }}
            >
              See How It Works
            </Button>
          </div>
        </div>
      </EditableSection>

      {/* Category Selection */}
      <section className="py-16 px-4" style={{ backgroundColor: 'white' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-light mb-3" style={{ color: earthTones.warmBrown }}>
              From community to excursion
            </h2>
            <p style={{ color: earthTones.stone }}>
              Collect preferences for every type of wellness gathering
            </p>
          </div>
          
          {/* Category Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {Object.entries(wellnessCategories).map(([key, category]) => {
              const isSelected = selectedCategory === key;
              const IconComponent = category.icon;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`
                    flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300
                    ${isSelected ? 'shadow-md' : 'hover:shadow-sm'}
                  `}
                  style={{
                    backgroundColor: isSelected ? earthTones.moss : 'white',
                    color: isSelected ? 'white' : earthTones.warmBrown,
                    border: `1px solid ${isSelected ? earthTones.moss : earthTones.sand}`
                  }}
                  data-testid={`category-${key}`}
                >
                  <IconComponent className="w-4 h-4" />
                  <span className="font-medium">{category.name}</span>
                </button>
              );
            })}
          </div>
          
          {/* Preference Cards */}
          <div className="max-w-4xl mx-auto">
            <Card 
              className="border-0 shadow-xl overflow-hidden"
              style={{ backgroundColor: earthTones.cream }}
            >
              {/* Card Header */}
              <div 
                className="px-8 py-6 flex items-center justify-between"
                style={{ backgroundColor: earthTones.sand }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: 'white' }}
                  >
                    <currentCategory.icon className="w-7 h-7" style={{ color: earthTones.moss }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium" style={{ color: earthTones.warmBrown }}>
                      {currentCategory.name}
                    </h3>
                    <p className="text-sm" style={{ color: earthTones.stone }}>
                      {currentCategory.description}
                    </p>
                  </div>
                </div>
                <Badge 
                  className="hidden sm:flex"
                  style={{ backgroundColor: earthTones.moss, color: 'white' }}
                >
                  {currentCategory.preferences.length} preference fields
                </Badge>
              </div>
              
              {/* Preference List */}
              <CardContent className="p-8">
                <p className="text-sm mb-6 font-medium" style={{ color: earthTones.stone }}>
                  Members share their preferences when joining:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {currentCategory.preferences.map((pref, idx) => {
                    const PrefIcon = pref.icon;
                    const isHovered = hoveredPreference === `${selectedCategory}-${idx}`;
                    return (
                      <div
                        key={idx}
                        className="rounded-2xl p-5 transition-all duration-300 cursor-default"
                        style={{ 
                          backgroundColor: isHovered ? 'white' : earthTones.cream,
                          border: `1px solid ${isHovered ? earthTones.sage : 'transparent'}`,
                          boxShadow: isHovered ? '0 4px 20px rgba(0,0,0,0.08)' : 'none'
                        }}
                        onMouseEnter={() => setHoveredPreference(`${selectedCategory}-${idx}`)}
                        onMouseLeave={() => setHoveredPreference(null)}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: earthTones.sand }}
                          >
                            <PrefIcon className="w-5 h-5" style={{ color: earthTones.moss }} />
                          </div>
                          <span className="font-medium" style={{ color: earthTones.warmBrown }}>
                            {pref.label}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {pref.options.map((opt, oidx) => (
                            <span
                              key={oidx}
                              className="text-xs px-3 py-1.5 rounded-full"
                              style={{ 
                                backgroundColor: 'white',
                                color: earthTones.stone,
                                border: `1px solid ${earthTones.sand}`
                              }}
                            >
                              {opt}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Demo Community Member Card */}
      <section className="py-20 px-4" style={{ backgroundColor: earthTones.cream }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-light mb-3" style={{ color: earthTones.warmBrown }}>
              Know every member's journey
            </h2>
            <p style={{ color: earthTones.stone }}>
              Each community member has a shareable wellness profile
            </p>
          </div>
          
          <div className="max-w-md mx-auto">
            <Card 
              className="border-0 shadow-2xl overflow-hidden"
              style={{ backgroundColor: 'white' }}
            >
              {/* Member Header */}
              <div 
                className="p-6"
                style={{ 
                  background: `linear-gradient(135deg, ${earthTones.moss} 0%, ${earthTones.sage} 100%)`
                }}
              >
                <div className="flex items-center gap-4">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-light"
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  >
                    {demoCommunityMember.avatar}
                  </div>
                  <div className="text-white">
                    <h4 className="text-xl font-medium">{demoCommunityMember.name}'s Wellness Profile</h4>
                    <p className="text-sm opacity-80">Member for {demoCommunityMember.memberSince}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {demoCommunityMember.tags.map((tag, idx) => (
                    <span 
                      key={idx}
                      className="text-xs px-3 py-1 rounded-full"
                      style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Preferences */}
              <CardContent className="p-6 space-y-4">
                <div 
                  className="rounded-2xl p-4"
                  style={{ backgroundColor: earthTones.cream }}
                >
                  <p className="text-xs font-medium mb-3" style={{ color: earthTones.stone }}>
                    WELLNESS PREFERENCES
                  </p>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span style={{ color: earthTones.stone }}>Focus</span>
                      <span className="font-medium" style={{ color: earthTones.warmBrown }}>
                        {demoCommunityMember.preferences.focus}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: earthTones.stone }}>Level</span>
                      <span className="font-medium" style={{ color: earthTones.warmBrown }}>
                        {demoCommunityMember.preferences.level}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span style={{ color: earthTones.stone }}>Style</span>
                      <span className="font-medium" style={{ color: earthTones.warmBrown }}>
                        {demoCommunityMember.preferences.style}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Interests */}
                <div 
                  className="rounded-2xl p-4"
                  style={{ backgroundColor: `${earthTones.sage}15` }}
                >
                  <p className="text-xs font-medium mb-2" style={{ color: earthTones.moss }}>
                    INTERESTS
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {demoCommunityMember.preferences.interests.map((interest, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-3 py-1.5 rounded-full"
                        style={{ backgroundColor: 'white', color: earthTones.moss }}
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Notes */}
                <div 
                  className="rounded-2xl p-4"
                  style={{ backgroundColor: `${earthTones.terracotta}15` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4" style={{ color: earthTones.terracotta }} />
                    <span className="text-xs font-medium" style={{ color: earthTones.terracotta }}>
                      NOTES FOR FACILITATORS
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: earthTones.warmBrown }}>
                    {demoCommunityMember.preferences.notes}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Community + Excursion Benefits */}
      <section className="py-20 px-4" style={{ backgroundColor: 'white' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-light mb-3" style={{ color: earthTones.warmBrown }}>
              Deepen connection at every touchpoint
            </h2>
            <p style={{ color: earthTones.stone }}>
              From daily community to transformative excursions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: "Community onboarding",
                description: "New members share their journey, intentions, and comfort levels before their first gathering."
              },
              {
                icon: Compass,
                title: "Curated excursions",
                description: "Match members to retreats and adventures based on their preferences and readiness."
              },
              {
                icon: Heart,
                title: "Held with care",
                description: "Facilitators know sensitivities, triggers, and needs - creating truly safe spaces."
              }
            ].map((benefit, idx) => {
              const BenefitIcon = benefit.icon;
              return (
                <Card 
                  key={idx}
                  className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300"
                  style={{ backgroundColor: earthTones.cream }}
                >
                  <CardContent className="p-8 text-center">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                      style={{ backgroundColor: earthTones.sand }}
                    >
                      <BenefitIcon className="w-8 h-8" style={{ color: earthTones.moss }} />
                    </div>
                    <h3 className="text-lg font-medium mb-3" style={{ color: earthTones.warmBrown }}>
                      {benefit.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: earthTones.stone }}>
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4" style={{ backgroundColor: earthTones.cream }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-light mb-3" style={{ color: earthTones.warmBrown }}>
              Simple for you, sacred for them
            </h2>
          </div>
          
          <div className="space-y-6">
            {[
              {
                step: "01",
                title: "Invite to your community",
                description: "Share your community link. Members complete a thoughtful wellness intake at their own pace."
              },
              {
                step: "02",
                title: "Understand before gathering",
                description: "View each member's preferences, sensitivities, and intentions in your community dashboard."
              },
              {
                step: "03",
                title: "Offer aligned excursions",
                description: "Invite members to retreats and experiences that match their journey and readiness level."
              }
            ].map((item, idx) => (
              <div 
                key={idx}
                className="flex gap-6 p-6 rounded-3xl transition-all duration-300 hover:shadow-lg"
                style={{ backgroundColor: 'white' }}
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 font-light text-xl"
                  style={{ backgroundColor: earthTones.moss, color: 'white' }}
                >
                  {item.step}
                </div>
                <div>
                  <h3 className="text-lg font-medium mb-2" style={{ color: earthTones.warmBrown }}>
                    {item.title}
                  </h3>
                  <p style={{ color: earthTones.stone }}>
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-4" style={{ backgroundColor: 'white' }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex justify-center gap-1 mb-6">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-current" style={{ color: earthTones.terracotta }} />
            ))}
          </div>
          <blockquote 
            className="text-xl sm:text-2xl font-light mb-6 leading-relaxed italic"
            style={{ color: earthTones.warmBrown }}
          >
            "We finally know our community beyond surface level. Our retreats went from good to transformational because we understand each person's journey."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center font-light"
              style={{ backgroundColor: earthTones.sand, color: earthTones.warmBrown }}
            >
              A
            </div>
            <div className="text-left">
              <p className="font-medium" style={{ color: earthTones.warmBrown }}>Aria Moon</p>
              <p className="text-sm" style={{ color: earthTones.stone }}>Founder, Roots & Rise Collective</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-24 px-4"
        style={{ 
          background: `linear-gradient(135deg, ${earthTones.moss} 0%, ${earthTones.sage} 100%)`
        }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light mb-4 text-white">
            Ready to deepen your community?
          </h2>
          <p className="text-lg mb-10 text-white/80">
            Join wellness leaders who are creating more intentional, connected experiences.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => setShowEarlyAccessModal(true)}
              size="lg"
              className="h-14 px-10 rounded-full text-base font-medium shadow-lg"
              style={{ backgroundColor: 'white', color: earthTones.moss }}
              data-testid="cta-join-btn"
            >
              Get Early Access
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="h-14 px-10 rounded-full text-base border-white/30 text-white hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.3)' }}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Talk to Us
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SharedFooter />

      {/* Early Access Modal */}
      <EarlyAccessModal
        open={showEarlyAccessModal}
        onOpenChange={setShowEarlyAccessModal}
        category="wellness"
      />
    </div>
  );
};

export default WellnessSalesPage;
